import { PlayArrow, Refresh, Warning } from '@mui/icons-material';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { usePluginActions, usePlugins } from 'api/plugins';
import { useNotifications } from 'components/notify';
import React, { useState } from 'react';

export default function PluginsPage() {
  const { data: pluginsData, error, mutate } = usePlugins();
  const { runPlugin } = usePluginActions();
  const { showNotification } = useNotifications();

  const [selectedPlugin, setSelectedPlugin] = useState<any>(null);
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [scope, setScope] = useState('');
  const [running, setRunning] = useState(false);

  const handleRunPlugin = async () => {
    if (!selectedPlugin || !scope.trim()) return;

    setRunning(true);
    try {
      const result = await runPlugin(selectedPlugin.id, scope);

      if (result.error) {
        showNotification(`Plugin execution failed: ${result.error}`, {
          variant: 'error',
        });
      } else {
        showNotification('Plugin executed successfully!', {
          variant: 'success',
        });
        setRunDialogOpen(false);
        setScope('');
        setSelectedPlugin(null);
      }
    } catch (error) {
      showNotification('Failed to run plugin', { variant: 'error' });
      console.error('Plugin run error:', error);
    } finally {
      setRunning(false);
    }
  };

  const handleRefresh = () => {
    mutate();
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Failed to load plugins. Please try again.
        </Alert>
      </Box>
    );
  }

  if (!pluginsData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Plugins
        </Typography>
        <IconButton onClick={handleRefresh} disabled={!pluginsData}>
          <Refresh />
        </IconButton>
      </Stack>

      {pluginsData.length === 0 ? (
        <Alert severity="info">
          No plugins available. Check back later for new plugins.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {pluginsData.map((plugin) => (
            <Grid item xs={12} sm={6} md={4} key={plugin.id}>
              <Card>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    mb={2}
                  >
                    <Typography variant="h6" component="h2">
                      {plugin.name}
                    </Typography>
                    <Chip
                      label={plugin.is_ready ? 'Ready' : 'Not Ready'}
                      color={plugin.is_ready ? 'success' : 'warning'}
                      size="small"
                    />
                  </Stack>

                  <Typography variant="body2" color="text.secondary" mb={1}>
                    Version: {plugin.version}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    ID: {plugin.id}
                  </Typography>
                </CardContent>

                <CardActions>
                  <Button
                    startIcon={<PlayArrow />}
                    variant="contained"
                    onClick={() => {
                      setSelectedPlugin(plugin);
                      setRunDialogOpen(true);
                    }}
                    fullWidth
                  >
                    Run Plugin
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Run Plugin Dialog */}
      <Dialog
        open={runDialogOpen}
        onClose={() => setRunDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Run Plugin: {selectedPlugin?.name}</DialogTitle>
        <DialogContent>
          <DialogContentText mb={2}>
            Enter the scope for this plugin execution. The scope defines the
            context in which the plugin will run.
          </DialogContentText>

          <Autocomplete
            freeSolo
            options={['entity.update']}
            value={scope}
            onChange={(event, newValue) => setScope(newValue || '')}
            onInputChange={(event, newInputValue) => setScope(newInputValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                margin="dense"
                label="Scope"
                fullWidth
                variant="outlined"
                placeholder="Enter plugin scope..."
                helperText="Define the scope for plugin execution"
              />
            )}
          />

          {selectedPlugin && !selectedPlugin.is_ready && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Warning sx={{ mr: 1 }} />
              This plugin is not ready for execution.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunDialogOpen(false)} disabled={running}>
            Cancel
          </Button>
          <Button
            onClick={handleRunPlugin}
            variant="contained"
            disabled={!scope.trim() || running}
            startIcon={running ? <CircularProgress size={16} /> : <PlayArrow />}
          >
            {running ? 'Running...' : 'Run Plugin'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
